import React, { useState, useEffect } from 'react';
import {
    RiImageAddLine,
    RiDeleteBinLine,
    RiExternalLinkLine,
    RiLoader4Line,
     RiTimeLine
} from 'react-icons/ri';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL, ASSET_URL } from '../../config';

const ManagePartners = () => {
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        logo_url: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        fetchPartners();
    }, []);

    const fetchPartners = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/partners/read.php`);
            const data = await response.json();
            if (response.ok) {
                setPartners(data.records || []);
            }
        } catch (error) {
            console.error('Error fetching partners:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setMessage({ type: '', text: '' });

        let finalLogoUrl = formData.logo_url;

        // 1. Upload file if selected
        if (selectedFile) {
            const uploadFormData = new FormData();
            uploadFormData.append('file', selectedFile);

            try {
                const uploadResponse = await fetch(`${API_BASE_URL}/upload.php`, {
                    method: 'POST',
                    body: uploadFormData
                });
                const uploadData = await uploadResponse.json();

                if (uploadResponse.ok) {
                    finalLogoUrl = uploadData.url;
                } else {
                    setMessage({ type: 'error', text: uploadData.message || 'Failed to upload logo.' });
                    setIsSubmitting(false);
                    return;
                }
            } catch (error) {
                setMessage({ type: 'error', text: 'Upload failed due to network error.' });
                setIsSubmitting(false);
                return;
            }
        } else if (!finalLogoUrl) {
            setMessage({ type: 'error', text: 'Please select a logo or provide a URL.' });
            setIsSubmitting(false);
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/partners/create.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...formData, logo_url: finalLogoUrl })
            });

            const data = await response.json();
            if (response.ok) {
                setMessage({ type: 'success', text: 'Global partner added successfully!' });
                setFormData({ name: '', logo_url: '' });
                setSelectedFile(null);
                setPreviewUrl('');
                fetchPartners();
            } else {
                setMessage({ type: 'error', text: data.message || 'Failed to add partner.' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'A network error occurred.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Are you sure you want to remove "${name}" from the global partners?`)) return;

        try {
            const response = await fetch(`${API_BASE_URL}/partners/delete.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id })
            });

            if (response.ok) {
                fetchPartners();
            } else {
                const data = await response.json();
                alert(data.message || "Failed to remove partner.");
            }
        } catch (error) {
            console.error('Error removing partner:', error);
        }
    };

    return (
        <div className="max-w-[1200px] mx-auto pb-20">
            <div className="mb-10">
                <h2 className="text-4xl font-black text-slate-900 tracking-tight">Collaboration</h2>
                <p className="text-slate-500 font-medium mt-2">Manage global partners and organizational alliances</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Addition Form */}
                <div className="lg:col-span-4">
                    <div className="bg-white p-8 rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.04)] border border-slate-100 sticky top-10">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="w-12 h-12 rounded-2xl bg-emerald-600 flex items-center justify-center text-white shadow-lg shadow-emerald-900/20">
                                <RiImageAddLine size={24} />
                            </div>
                            <div>
                                <h3 className="font-black text-slate-900 leading-tight">Add Partner</h3>
                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">New Alliance</p>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2 pl-1">Partner Name</label>
                                <input
                                    type="text"
                                    placeholder="e.g. World Wildlife Fund"
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full p-4 bg-slate-50 rounded-2xl border-none text-sm font-bold focus:ring-2 focus:ring-emerald-500 transition-all"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2 pl-1">Partner Logo Asset</label>
                                <div className="space-y-4">
                                    <div
                                        onClick={() => document.getElementById('logo-upload').click()}
                                        className="w-full h-32 border-2 border-dashed border-slate-200 rounded-2xl flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/30 transition-all overflow-hidden relative group"
                                    >
                                        {previewUrl ? (
                                            <>
                                                <img src={previewUrl} alt="Preview" className="h-full w-full object-contain p-4 transition-transform group-hover:scale-105" />
                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white text-[10px] font-black uppercase tracking-widest">
                                                    Change Logo
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <RiImageAddLine className="text-slate-300 group-hover:text-emerald-500 transition-colors" size={32} />
                                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Click to upload png/jpg</span>
                                            </>
                                        )}
                                    </div>
                                    <input
                                        type="file"
                                        id="logo-upload"
                                        className="hidden"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                    />

                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <RiExternalLinkLine className="text-slate-300" />
                                        </div>
                                        <input
                                            type="url"
                                            placeholder="...or paste external logo URL"
                                            value={formData.logo_url}
                                            onChange={e => {
                                                setFormData({ ...formData, logo_url: e.target.value });
                                                if (!selectedFile) setPreviewUrl(e.target.value);
                                            }}
                                            className="w-full pl-11 pr-4 py-3 bg-slate-50 rounded-2xl border-none text-[10px] font-bold focus:ring-2 focus:ring-emerald-500 transition-all"
                                        />
                                    </div>
                                </div>
                                <p className="mt-2 text-[10px] text-slate-400 font-medium px-1 italic">Note: Use high-quality transparent PNG logos for best results.</p>
                            </div>

                            <AnimatePresence>
                                {message.text && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className={`p-4 rounded-2xl text-xs font-bold ${message.type === 'success' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}
                                    >
                                        {message.text}
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <button
                                disabled={isSubmitting}
                                className="w-full bg-slate-900 text-white font-black py-4 rounded-2xl text-xs uppercase tracking-[0.2em] hover:bg-slate-800 transition-all shadow-xl shadow-slate-900/20 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {isSubmitting ? <RiLoader4Line className="animate-spin" size={20} /> : 'Register Partner'}
                            </button>
                        </form>
                    </div>
                </div>

                {/* Partners List */}
                <div className="lg:col-span-8">
                    <div className="bg-white rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.04)] border border-slate-100 overflow-hidden">
                        <div className="p-8 border-b border-slate-50 flex items-center justify-between">
                            <h3 className="font-black text-slate-900 uppercase tracking-widest text-xs">Active Collaborators</h3>
                            <span className="bg-slate-50 px-3 py-1 rounded-full text-[10px] font-black text-slate-400">{partners.length} Entities</span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-50">
                            {loading ? (
                                <div className="col-span-2 flex justify-center py-20 text-emerald-500 bg-white">
                                    <RiLoader4Line className="animate-spin text-4xl" />
                                </div>
                            ) : partners.length === 0 ? (
                                <div className="col-span-2 py-20 text-center text-slate-400 font-medium bg-white">No active partners found.</div>
                            ) : (
                                partners.map((partner) => (
                                    <motion.div
                                        key={partner.id}
                                        layout
                                        className="bg-white p-6 flex items-center justify-between group hover:bg-slate-50/50 transition-colors"
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="w-16 h-16 rounded-2xl bg-white border border-slate-100 flex items-center justify-center p-3 group-hover:shadow-md transition-all overflow-hidden">
                                                <img
                                                    src={partner.logo_url.startsWith('http') ? partner.logo_url : `${ASSET_URL}${partner.logo_url}`}
                                                    alt={partner.name}
                                                    className="max-h-full w-auto object-contain grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300"
                                                />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-slate-900 leading-tight">{partner.name}</h4>
                                                <div className="flex items-center gap-1 text-[10px] font-medium text-slate-400 mt-1">
                                                    <RiTimeLine />
                                                    {new Date(partner.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                </div>
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => handleDelete(partner.id, partner.name)}
                                            className="p-3 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                                            title="Remove Collaboration"
                                        >
                                            <RiDeleteBinLine size={20} />
                                        </button>
                                    </motion.div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManagePartners;
